<template>
  <div class="max-w-3xl mx-auto">
    <div>
      <label for="title" class="block text-sm font-medium leading-5 text-gray-700">
        제목
      </label>
      <input
        id="title"
        type="text"
        v-model.trim="$v.form.title.$model"
        class="my-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
        :class="{ 'border-red-500': $v.form.title.$error }"
      >
      <div
        v-if="$v.form.title.$error || errors.title"
        class="text-red-500 text-xs italic"
      >
        필수입력 항목입니다.
      </div>
    </div>
    <div class="mt-3">
      <label for="body" class="block text-sm font-medium leading-5 text-gray-700">
        내용
      </label>
      <div class="p-1">
        <editor
          v-model.trim="$v.form.body.$model"
          api-key="rueeof4lvm2pouq5kntgcqqvu7lcz8sa619ugak601em924u"
          :init="{
            height: 400,
            language: 'ko_KR',
            language_url: '/js/ko_KR.js',
            menubar: false,
            plugins: [
              'advlist autolink lists link image charmap print preview anchor',
              'searchreplace visualblocks code fullscreen',
              'insertdatetime media table paste code wordcount'
            ],
            toolbar: toolbar
          }"
        />
      </div>
      <div
        v-if="$v.form.body.$error || errors.body"
        class="text-red-500 text-xs italic"
      >
        필수입력 항목입니다.
      </div>
    </div>
    <div class="mt-3">
      <label for="username" class="block text-sm font-medium leading-5 text-gray-700">
        이름
      </label>
      <div>
        <input
          id="username"
          type="text"
          v-model.trim="$v.form.username.$model"
          class="my-1 form-input block w-2/3 py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          :class="{ 'border-red-500': $v.form.username.$error }"
        >
        <div
          v-if="errors.username"
          class="text-red-500 text-xs italic"
        >
          {{ errors.username[0] }}
        </div>
        <template v-if="$v.form.username.$error">
          <div v-if="!$v.form.username.required" class="text-red-500 text-xs italic">필수입력 항목입니다.</div>
          <div v-if="!$v.form.username.minLength" class="text-red-500 text-xs italic">{{ $v.form.username.$params.minLength.min }}자 이상 입력하세요.</div>
        </template>
      </div>
    </div>
    <div v-if="!authenticated" class="mt-3">
      <label for="password" class="block text-sm font-medium leading-5 text-gray-700">
        비밀번호 <span v-if="mode === 'edit'" class="text-gray-400 tracking-tighter">(작성시의 비밀번호와 일치하지 않으면 수정불가)</span>
      </label>
      <div>
        <input
          id="password"
          type="password"
          v-model.trim="$v.form.password.$model"
          class="my-1 form-input block w-2/3 py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          :class="{ 'border-red-500': $v.form.password.$error }"
        >
        <div
          v-if="errors.password"
          class="text-red-500 text-xs italic"
        >
          {{ errors.password[0] }}
        </div>
        <template v-if="$v.form.password.$error">
          <div v-if="!$v.form.password.required" class="text-red-500 text-xs italic">필수입력 항목입니다.</div>
          <div v-if="!$v.form.password.minLength" class="text-red-500 text-xs italic">{{ $v.form.password.$params.minLength.min }}자 이상 입력하세요.</div>
        </template>
      </div>
    </div>
    <div v-if="needCaptcha" class="mt-3">
      <label class="block text-sm font-medium leading-5 text-gray-700">
        스팸방지 문자
        <span class="text-gray-400 tracking-tighter">(대소문자 구분 없음)</span>
      </label>
      <div>
        <div class="my-1 flex items-center">
          <input
            id="captcha"
            type="text"
            maxlength="4"
            v-model.trim="$v.form.captcha.$model"
            class="form-input block w-1/3 py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
            :class="{ 'border-red-500': $v.form.captcha.$error }"
          >
          <get-captcha ref="captchaComponent" />
        </div>
        <div v-if="errors.captcha" class="text-red-500 text-xs italic">{{ errors.captcha[0] }}</div>
        <template v-if="$v.form.captcha.$error">
          <div v-if="!$v.form.captcha.required" class="text-red-500 text-xs italic">필수입력 항목입니다.</div>
          <div v-if="!$v.form.captcha.minLength" class="text-red-500 text-xs italic">{{ $v.form.captcha.$params.minLength.min }}자 이상 입력하세요.</div>
        </template>
      </div>
    </div>

    <drop-zone
      :max="maxImage"
      :files="post ? post.files : []" @selected="selected"
    />

    <div v-if="authenticated && postNode === 'story'" class="mt-4">
      <div class="max-w-xs rounded-md shadow-sm">
        <select
          id="type"
          class="block form-select w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          v-model="form.type"
        >
          <option>칼럼</option>
          <option>영상</option>
          <option>사진</option>
        </select>
      </div>
    </div>

    <div v-if="authenticated && postNode !== 'free'" class="flex items-center mt-6">
      <input
        id="home"
        type="checkbox"
        class="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
        v-model="form.home"
      >
      <label for="home" class="ml-2 block text-sm leading-5 text-gray-900">
        메인페이지 노출
      </label>
    </div>

    <div class="mt-6">
      <span class="inline-flex rounded-md shadow-sm">
        <button
          type="button"
          @click="onSubmit"
          class="inline-flex justify-center py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo active:bg-indigo-700 transition duration-150 ease-in-out"
          :disabled="$v.$invalid || saving"
          :class="{ 'opacity-50 cursor-not-allowed': $v.$invalid || saving }"
        >
          {{ saving ? '처리 중...' : '저장' }}
        </button>
      </span>
      <button
        type="button"
        @click="goBack"
        class="py-2 px-4 text-sm hover:text-gray-600"
      >
        취소
      </button>
    </div>
  </div>
</template>

<script>
import Editor from '@tinymce/tinymce-vue'
import { validationMixin } from 'vuelidate'
import { required, minLength, minValue } from 'vuelidate/lib/validators'
import DropZone from './DropZone'
import GetCaptcha from './GetCaptcha'

export default {
  components: {
    Editor,
    DropZone,
    GetCaptcha
  },

  mixins: [ validationMixin ],

  props: {
    post: Object,
    postNode: String,
    auth: String,
  },

  data() {
    return {
      mode: 'new',
      saving: false,
      errors: [],
      maxImage: 3,
      form: {
        type: '칼럼',
        title: null,
        body: null,
        username: null,
        password: null,
        captcha: null,
        files: []
      },
      toolbar: 'undo redo | bold italic',
    }
  },

  validations() {
    let validator = {
      form: {
        title: {
          required
        },
        body: {
          required
        },
        username: {
          required,
          minLength: minLength(2)
        }
      },
    }

    if (!this.authenticated) {
      validator.form['password'] = {
        required,
        minLength: minLength(4)
      }
    }
    if (this.needCaptcha) {
      validator.form['captcha'] = {
        required,
        minLength: minLength(4)
      }
    }

    return validator
  },

  created() {
    if (this.post) {
      this.mode = 'edit'
      this.post.password = null
      this.form = this.post
    }

    if (this.auth) {
      this.maxImage = 10
      this.form.username = this.auth
      this.toolbar = 'undo redo | bold italic | forecolor | bullist numlist | alignleft aligncenter alignright | link image media code'
    }
  },

  computed: {
    authenticated() {
      return this.auth !== ''
    },

    needCaptcha() {
      return this.auth === '' && this.mode === 'new'
    },

    ready() {
      return this.needCaptcha
          && !this.$v.form.body.$invalid
          && !this.$v.form.title.$invalid
          && !this.$v.form.username.$invalid
          && !this.$v.form.password.$invalid
    },

    endpoint() {
      let endpoint = `/posts/${this.postNode}`
      return this.mode === 'new' ?  endpoint : `${endpoint}/${this.post.id}`
    },
  },

  watch: {
    ready(val) {
      if (!val) {
        return false
      }

      this.resetCaptcha()
    }
  },

  methods: {
    goBack() {
      location.href = this.endpoint
    },

    resetCaptcha() {
      if (!this.needCaptcha) {
        return
      }

      this.$refs.captchaComponent.getCaptcha()
    },

    async onSubmit() {
      this.$v.$touch()

      if (this.$v.$invalid) {
        return false
      }

      this.saving = true

      try {
        const method = this.mode === 'new' ? 'post' : 'patch'
        const response = await axios[method](this.endpoint, this.form)
        location.href = this.endpoint
      } catch (error) {
        this.saving = false
        if (error.response.status !== 422) {
          console.log('알 수 없는 오류가 발생했습니다.')
        }

        this.resetCaptcha()
        this.errors = error.response.data.errors || []
      }
      return
    },

    selected(items) {
      this.form.files = items
    }
  }
}
</script>
